// The public compatibility checker's engine — plan S24, ADR 011 amendment
// 2026-09-17. A visitor picks a receiver, speakers by role and a layout; no
// room exists. This builds a synthetic document in a neutral standard room
// (ITU angles, one seat), runs the real `checkBuild` on it, and keeps only
// what is true of the PRODUCTS: rules that need a real room (angles, seat
// distance, wall gaps, projector throw) are listed as room checks instead,
// never as issues. Pure: no DOM, no fetch.

import {
  createBlankDoc,
  faceToward,
  normalizeEditorDoc,
  overheadCountForLayout,
  type EditorDoc,
  type GearEntry,
  type SpeakerEntry,
  type SpeakerLayout,
  type SpeakerRole,
  type SpeakerSubkind,
  type Vec3,
} from '@roomtreatment/core';
import { PRODUCT_ID, type Product, type ProductType } from '@roomtreatment/av-catalog';
import type { BuildReport, Channel, Issue, RuleId } from './types';
import { checkBuild } from './checkBuild';
import { RULE_IDS } from './rules/registry';
import { chainFit, roomRequirements, type ChainFit, type RoomRequirements } from './requirements';
import { productName } from './copy';

// ── Rule classification ─────────────────────────────────────────────────

/** Rules that read only product specs: they give the same answer in any room. */
export const ROOM_INDEPENDENT_RULES = [
  'AVR-01', // height decoding
  'AVR-02', // processing channels
  'AVR-03', // amplifier channels
  'AVR-04', // pre-outs
  'IMP-01', // impedance
  'CON-02', // subwoofer inputs vs sub outs
  'PHY-05', // front speakers match
] as const satisfies readonly RuleId[];

/** Rules that need the real room: positions, seat distance, walls, throw. */
export const ROOM_DEPENDENT_RULES = [
  'LAY-02', // bed speaker angle from the seat
  'LAY-03', // height speaker elevation from the seat
  'PWR-02', // peak level at the real seat distance
  'PHY-02', // rear port gap to the wall
  'PRJ-01', // projector throw distance to the screen
] as const satisfies readonly RuleId[];

const INDEPENDENT = new Set<RuleId>(ROOM_INDEPENDENT_RULES);

/** What each room check looks at, in words a first-time visitor understands. */
export const ROOM_CHECK_LABEL: Record<(typeof ROOM_DEPENDENT_RULES)[number], string> = {
  'LAY-02': 'Speaker angles from your seat',
  'LAY-03': 'Height speaker angles above your seat',
  'PWR-02': 'Loudness at your real seat distance',
  'PHY-02': 'Rear port distance from the wall',
  'PRJ-01': 'Projector throw distance',
};

// ── Selection ───────────────────────────────────────────────────────────

/** Layouts the checker offers, in display order. */
export const SYSTEM_LAYOUTS = ['stereo', '5.1', '5.1.2', '5.1.4', '7.1', '7.1.2', '7.1.4'] as const satisfies readonly SpeakerLayout[];
export type SystemLayout = (typeof SYSTEM_LAYOUTS)[number];

export interface SystemSelection {
  layout: SystemLayout;
  /** AV receiver or processor. */
  avr?: string;
  /** Power amplifiers, in order. */
  amps?: string[];
  fronts?: string;
  center?: string;
  /** Surrounds; in a 7.1 layout the same model also fills the rear surrounds. */
  surrounds?: string;
  /** Height speakers (front and, in a .4 layout, rear). */
  heights?: string;
  sub?: string;
  /** How many of `sub`; 1 when absent. */
  subCount?: number;
}

/**
 * The checker's worked example, and the result crawlers see: an all-ELAC
 * Debut 3.0 5.1 on a Denon AVR-S570BT. The DF63 towers are 4 Ω and the
 * receiver is rated for 6 Ω and up — the same real IMP-01 catch the landing
 * page's gear demo shows. Every other check passes.
 */
export const EXAMPLE_SELECTION: SystemSelection = {
  layout: '5.1',
  avr: 'denon.avr-s570bt',
  fronts: 'elac.df63',
  center: 'elac.dc53',
  surrounds: 'elac.db53',
  sub: 'klipsch.r-120sw',
};

export type SystemRole ='avr' | 'amps' | 'fronts' | 'center' | 'surrounds' | 'heights' | 'sub';

/** Product types each role accepts. */
export const ROLE_TYPES: Record<SystemRole, readonly ProductType[]> = {
  avr: ['av_receiver', 'av_processor'],
  amps: ['power_amp'],
  fronts: ['speaker_tower', 'speaker_bookshelf'],
  center: ['speaker_center', 'speaker_bookshelf'],
  surrounds: ['speaker_surround', 'speaker_bookshelf', 'speaker_tower'],
  heights: ['speaker_height', 'speaker_in_ceiling'],
  sub: ['subwoofer'],
};

export const MAX_SUBS = 4;

/** Roles this layout uses, in display order. `required` roles must be picked before the checker can pass. */
export function rolesForLayout(layout: SystemLayout): { role: SystemRole; required: boolean }[] {
  const bed = layout !== 'stereo';
  return [
    { role: 'avr', required: true },
    { role: 'fronts', required: true },
    ...(bed ? [{ role: 'center' as const, required: true }, { role: 'surrounds' as const, required: true }] : []),
    ...(overheadCountForLayout(layout) > 0 ? [{ role: 'heights' as const, required: true }] : []),
    // Every x.1 layout has a subwoofer; stereo may add one.
    { role: 'sub', required: bed },
    { role: 'amps', required: false },
  ];
}

function acceptsType(role: SystemRole, product: Product | undefined): boolean {
  return !!product && ROLE_TYPES[role].includes(product.type);
}

// ── Synthetic document ──────────────────────────────────────────────────

/** The neutral room: 5 m wide, 6 m long, 2.8 m high, seat on the centre line. */
const ROOM = { Lx: 5, Ly: 6, H: 2.8 } as const;
const EAR: Vec3 = [2.5, 1.2, 3.5];
/** Every speaker sits this far from the seat, heights included. */
export const STANDARD_SEAT_DISTANCE_M = 2;

interface Slot {
  id: string;
  role: 'fronts' | 'center' | 'surrounds' | 'heights';
  azDeg: number;
  /** Elevation above the ear, degrees. */
  elDeg: number;
  speakerRole: SpeakerRole;
}

const bed = (id: string, role: Slot['role'], azDeg: number, speakerRole: SpeakerRole = null): Slot =>
  ({ id, role, azDeg, elDeg: 0, speakerRole });
const high = (id: string, azDeg: number): Slot => ({ id, role: 'heights', azDeg, elDeg: 45, speakerRole: null });

/** ITU-R BS.775 / Dolby angles — the same azimuths the engine's bed slots target. */
function slotsFor(layout: SystemLayout): Slot[] {
  const slots: Slot[] = [bed('fl', 'fronts', -30, 'main_l'), bed('fr', 'fronts', 30, 'main_r')];
  if (layout.startsWith('5.1')) {
    slots.push(bed('c', 'center', 0), bed('sl', 'surrounds', -110), bed('sr', 'surrounds', 110));
  } else if (layout.startsWith('7.1')) {
    slots.push(
      bed('c', 'center', 0),
      bed('sl', 'surrounds', -90), bed('sr', 'surrounds', 90),
      bed('sbl', 'surrounds', -135), bed('sbr', 'surrounds', 135),
    );
  }
  const heights = overheadCountForLayout(layout);
  if (heights === 2) slots.push(high('hl', -90), high('hr', 90));
  if (heights === 4) slots.push(high('tfl', -45), high('tfr', 45), high('trl', -135), high('trr', 135));
  return slots;
}

function slotPosition(azDeg: number, elDeg: number): Vec3 {
  const az = (azDeg * Math.PI) / 180;
  const el = (elDeg * Math.PI) / 180;
  const horiz = STANDARD_SEAT_DISTANCE_M * Math.cos(el);
  // Front wall at z = 0, forward is −Z, positive azimuth to the right (README "Forward direction").
  return [EAR[0] + horiz * Math.sin(az), EAR[1] + STANDARD_SEAT_DISTANCE_M * Math.sin(el), EAR[2] - horiz * Math.cos(az)];
}

function subkindFor(role: Slot['role'], product: Product | undefined): SpeakerSubkind {
  if (role === 'heights') return 'overhead';
  return product?.type === 'speaker_tower' ? 'floorstanding' : 'bookshelf';
}

function productFor(id: string | undefined, role: SystemRole, catalog: ReadonlyMap<string, Product>): Product | undefined {
  const product = id ? catalog.get(id) : undefined;
  return acceptsType(role, product) ? product : undefined;
}

function subCountOf(selection: SystemSelection): number {
  const n = Math.round(selection.subCount ?? 1);
  return Math.min(MAX_SUBS, Math.max(1, Number.isFinite(n) ? n : 1));
}

/**
 * The selection as an `EditorDoc`: every speaker of the layout on a 2 m ring
 * around one seat at its standard angle, bound to the picked product (an
 * unpicked role stays a generic speaker, so channel counts stay right), and
 * the receiver and amps at the front wall. Ids that don't resolve to a
 * product of the role's type are left unbound.
 */
export function buildSystemDoc(selection: SystemSelection, catalog: ReadonlyMap<string, Product>): EditorDoc {
  const speakers: SpeakerEntry[] = slotsFor(selection.layout).map((slot) => {
    const product = productFor(selection[slot.role], slot.role, catalog);
    const position = slotPosition(slot.azDeg, slot.elDeg);
    return {
      id: slot.id,
      subkind: subkindFor(slot.role, product),
      role: slot.speakerRole,
      freqResponse: null,
      position,
      rotationY: faceToward(position, EAR),
      ...(product ? { productId: product.id } : {}),
    };
  });

  const subProduct = productFor(selection.sub, 'sub', catalog);
  const subs = subProduct ? subCountOf(selection) : selection.layout === 'stereo' ? 0 : 1;
  for (let i = 0; i < subs; i++) {
    const position: Vec3 = [i % 2 === 0 ? 0.6 : ROOM.Lx - 0.6, 0.25, 0.6 + 0.8 * Math.floor(i / 2)];
    speakers.push({
      id: `sub-${i + 1}`,
      subkind: 'subwoofer',
      role: null,
      freqResponse: null,
      position,
      rotationY: faceToward(position, EAR),
      ...(subProduct ? { productId: subProduct.id } : {}),
    });
  }

  const gear: GearEntry[] = [];
  const avr = productFor(selection.avr, 'avr', catalog);
  if (avr) gear.push({ id: 'avr', productId: avr.id, position: [ROOM.Lx / 2, 0, 0.3], rotationY: 0 });
  (selection.amps ?? []).forEach((id, i) => {
    const amp = productFor(id, 'amps', catalog);
    if (amp) gear.push({ id: `amp-${i + 1}`, productId: amp.id, position: [ROOM.Lx / 2 + 0.6 * (i + 1), 0, 0.3], rotationY: 0 });
  });

  const blank = createBlankDoc();
  return normalizeEditorDoc({
    ...blank,
    dims: { ...ROOM },
    speakers,
    listener: { id: 'seat', position: EAR, rotationY: Math.PI },
    gear,
    speakerSetup: { ...blank.speakerSetup, layout: selection.layout, sub: subs },
  });
}

// ── Check ───────────────────────────────────────────────────────────────

export type SystemVerdict = 'pass' | 'issues' | 'incomplete';

export interface UnknownSpec {
  rule: RuleId;
  productId: string;
  /** One sentence: which published spec is missing. */
  text: string;
}

export interface SystemReport {
  /** `issues` when any room-independent issue fired; otherwise `incomplete`
   *  while a required role is unpicked or a check could not run; else `pass`.
   *  An unknown never passes. */
  verdict: SystemVerdict;
  /** Room-independent issues only, errors first. */
  issues: Issue[];
  /** Room-independent checks that could not run for a missing spec. */
  unknown: UnknownSpec[];
  /** Required roles with no product picked. */
  missing: SystemRole[];
  /** Room-dependent rules that apply to this system: checked in the editor, not here. */
  roomChecks: { rule: RuleId; label: string }[];
  /** Channel of every speaker in the synthetic doc, keyed by speaker id. */
  channels: Record<string, Channel>;
  /** The receiver and amps against the speakers, at the standard seat distance. Null without a receiver. */
  headroom: { chain: ChainFit; needs: RoomRequirements; seatDistanceM: number } | null;
}

function unknownText(rule: RuleId, product: Product): string {
  const name = productName(product);
  if (rule === 'IMP-01') {
    return product.specs.kind === 'speaker'
      ? `${name} doesn't publish its nominal impedance, so the impedance check can't run.`
      : `${name} doesn't publish its minimum speaker impedance, so the impedance check can't run.`;
  }
  return `${name} is missing a spec this check needs.`;
}

function roomChecksFor(doc: EditorDoc, report: BuildReport, catalog: ReadonlyMap<string, Product>, hasAmplifier: boolean): SystemReport['roomChecks'] {
  const bound = doc.speakers.map((s) => (s.productId ? catalog.get(s.productId) : undefined));
  const applies: Record<(typeof ROOM_DEPENDENT_RULES)[number], boolean> = {
    'LAY-02': Object.values(report.channels).some((c) => c !== 'LFE' && !c.startsWith('T')),
    'LAY-03': Object.values(report.channels).some((c) => c.startsWith('T')),
    'PWR-02': hasAmplifier && bound.some((p) => p?.specs.kind === 'speaker' && !p.specs.active.v),
    'PHY-02': bound.some((p) => (p?.specs.kind === 'speaker' || p?.specs.kind === 'subwoofer') && p.specs.port?.v === 'rear'),
    // The checker places no projector.
    'PRJ-01': false,
  };
  return ROOM_DEPENDENT_RULES.filter((rule) => applies[rule]).map((rule) => ({ rule, label: ROOM_CHECK_LABEL[rule] }));
}

export function checkSystem(selection: SystemSelection, catalog: ReadonlyMap<string, Product>): SystemReport {
  const doc = buildSystemDoc(selection, catalog);
  const report = checkBuild(doc, catalog, { hasRoomResults: true });

  const issues = report.issues.filter((i) => INDEPENDENT.has(i.rule));
  const unknown: UnknownSpec[] = report.unknown
    .filter((u) => INDEPENDENT.has(u.rule))
    .flatMap((u) => u.productIds.flatMap((productId) => {
      const product = catalog.get(productId);
      return product ? [{ rule: u.rule, productId, text: unknownText(u.rule, product) }] : [];
    }));

  const missing = rolesForLayout(selection.layout)
    .filter(({ role, required }) => required && role !== 'amps' && !productFor(selection[role] as string | undefined, role, catalog))
    .map(({ role }) => role);

  const hasAvr = (doc.gear ?? []).some((g) => g.id === 'avr');
  const headroom = hasAvr
    ? (() => {
      const needs = roomRequirements(doc, catalog);
      return { chain: chainFit(doc, catalog, needs), needs, seatDistanceM: STANDARD_SEAT_DISTANCE_M };
    })()
    : null;

  const verdict: SystemVerdict = issues.length > 0 ? 'issues' : missing.length > 0 || unknown.length > 0 ? 'incomplete' : 'pass';

  return {
    verdict,
    issues,
    unknown,
    missing,
    roomChecks: roomChecksFor(doc, report, catalog, hasAvr),
    channels: report.channels,
    headroom,
  };
}

// ── URL hash ────────────────────────────────────────────────────────────

/** Hash key per role, in the order they are written (plan S24 contract, plus `amp` and `subs`). */
const HASH_KEYS = [
  ['avr', 'avr'],
  ['amps', 'amp'],
  ['fronts', 'fl'],
  ['center', 'c'],
  ['surrounds', 's'],
  ['heights', 'h'],
  ['sub', 'sub'],
] as const satisfies readonly (readonly [SystemRole, string])[];

/** `avr=…&fl=…&layout=5.1`, without the leading `#`. Empty roles are left out. */
export function encodeSelection(selection: SystemSelection): string {
  const parts: string[] = [];
  for (const [role, key] of HASH_KEYS) {
    const value = role === 'amps' ? (selection.amps ?? []).join(',') : selection[role];
    if (value) parts.push(`${key}=${value}`);
  }
  if (selection.sub && selection.subCount && selection.subCount > 1) parts.push(`subs=${selection.subCount}`);
  parts.push(`layout=${selection.layout}`);
  return parts.join('&');
}

function isLayout(v: string | undefined): v is SystemLayout {
  return !!v && (SYSTEM_LAYOUTS as readonly string[]).includes(v);
}

/**
 * Reads a hash (with or without `#`). Unknown keys, malformed ids and — when
 * `catalog` is given — ids that aren't a product of the role's type are
 * dropped. Without a `layout`, the smallest layout that uses every picked
 * role is chosen, so a product page can link with just its own role.
 */
export function decodeSelection(hash: string, catalog?: ReadonlyMap<string, Product>): SystemSelection {
  const params = new Map<string, string>();
  for (const pair of hash.replace(/^#/, '').split('&')) {
    const eq = pair.indexOf('=');
    if (eq <= 0) continue;
    let value: string;
    try {
      value = decodeURIComponent(pair.slice(eq + 1)).trim();
    } catch {
      continue;
    }
    if (value) params.set(pair.slice(0, eq), value);
  }

  const valid = (id: string, role: SystemRole) =>
    PRODUCT_ID.test(id) && (!catalog || acceptsType(role, catalog.get(id)));

  const selection: Omit<SystemSelection, 'layout'> = {};
  for (const [role, key] of HASH_KEYS) {
    const raw = params.get(key);
    if (!raw) continue;
    if (role === 'amps') {
      const amps = raw.split(',').map((s) => s.trim()).filter((id) => valid(id, 'amps'));
      if (amps.length > 0) selection.amps = amps;
    } else if (valid(raw, role)) {
      selection[role] = raw;
    }
  }
  const subs = Number(params.get('subs'));
  if (selection.sub && Number.isInteger(subs) && subs > 1 && subs <= MAX_SUBS) selection.subCount = subs;

  const layoutParam = params.get('layout');
  const layout: SystemLayout = isLayout(layoutParam)
    ? layoutParam
    : selection.heights ? '5.1.2' : selection.center || selection.surrounds ? '5.1' : 'stereo';
  return { layout, ...selection };
}
